import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useOrganisation } from '@/contexts/OrganisationContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles: Array<'admin' | 'doctor' | 'sales_rep' | 'company_admin'>;
  allowEnterpriseAssetManagers?: boolean;
}

export function ProtectedRoute({
  children,
  allowedRoles,
  allowEnterpriseAssetManagers = false,
}: ProtectedRouteProps) {
  const { user, isAuthenticated, loading, hasAnyRole } = useAuth();
  const { canCreateAssets } = useOrganisation();
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Not logged in - redirect to home
  if (!isAuthenticated || !user) {
    return <Navigate to="/" replace />;
  }
  
  // Logged in but wrong role - show access denied
  const enterpriseAssetAccess = allowEnterpriseAssetManagers && canCreateAssets;
  if (!hasAnyRole(allowedRoles) && !enterpriseAssetAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md px-6">
          <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock size={40} className="text-destructive" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-3">Access Denied</h1>
          <p className="text-muted-foreground mb-6">
            You don't have permission to access this page. This area is restricted to administrators only.
            {user.roles.length > 0 && (
              <span className="block mt-2 text-sm">
                Your roles: {user.roles.join(', ')}
              </span>
            )}
          </p>
          <Button 
            onClick={() => window.location.href = '/'} 
            className="bg-primary hover:bg-primary/90"
          >
            Return to Dashboard
          </Button>
        </div>
      </div>
    );
  }
  
  return <>{children}</>;
}
